<template>
  <Modal @close="$emit('close')">
    <div
      class="card"
      :style="{
        'font-family': fontFamily,
      }"
    >
      <div class="card__image">
        <img :src="selectedField.image || HOLDER_URL" />
      </div>
      <div class="card__info info">
        <div
          v-if="selectedField.title"
          class="info__title"
          v-html="selectedField.title"
        />
        <div
          v-if="selectedField.shortDescription"
          class="info__short-description"
          v-html="selectedField.shortDescription"
        />
        <div
          v-if="selectedField.volume"
          class="info__volume"
          v-html="selectedField.volume"
        />

        <div
          v-if="selectedField.wbLink || selectedField.ozonLink"
          class="info__buttons"
        >
          <a
            v-if="selectedField.wbLink"
            :href="selectedField.wbLink"
            title="Wildberries"
            target="_blank"
            >Wildberries</a
          >

          <a
            v-if="selectedField.ozonLink"
            :href="selectedField.ozonLink"
            title="Ozon"
            target="_blank"
            >OZON</a
          >
        </div>

        <div
          v-if="selectedTab"
          class="info__tabs tabs"
        >
          <div class="tabs__nav">
            <template
              v-for="tab in TABS"
              :key="tab.field"
            >
              <div
                v-if="selectedField[tab.field]"
                :class="[
                  'tabs__nav-item',
                  { 'tabs__nav-item--selected': selectedTab === tab.field },
                ]"
                @click="selectedTab = tab.field"
                v-html="tab.label"
              />
            </template>
          </div>
          <div
            class="tabs__content"
            v-html="selectedField[selectedTab]"
          />
        </div>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import type { Types } from '@/entities/component-template/C5/T10';

import { Modal } from '@/shared/ui/modal';

import { HOLDER_URL } from '@/constants';

interface Props {
  selectedField: Types.IModalSelectedField;
  fontFamily?: string;
}

const { selectedField, fontFamily = 'Jost' } = defineProps<Props>();

defineEmits(['close']);

enum TAB_FIELDS {
  DESCRIPTION = 'description',
  HOW_TO_USE = 'howToUse',
  INGREDIENTS = 'ingredients',
}

const TABS = [
  {
    field: TAB_FIELDS.DESCRIPTION,
    label: 'Описание',
  },
  {
    field: TAB_FIELDS.HOW_TO_USE,
    label: 'Применение',
  },
  {
    field: TAB_FIELDS.INGREDIENTS,
    label: 'Состав',
  },
];

const selectedFieldFirstTab = Object.values(TAB_FIELDS).find(
  (tab: TAB_FIELDS) => selectedField[tab],
);

const selectedTab = ref(selectedFieldFirstTab);
</script>

<style lang="scss" scoped>
.card {
  width: 1100px;
  padding: 40px;
  display: flex;
  gap: 40px;
  color: #000;

  &__image,
  &__info {
    flex: 1;
  }

  &__image {
    img {
      aspect-ratio: 1;
      max-width: 100%;
      max-height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  .info {
    & > *:first-child {
      margin-top: 0;
    }

    &__title {
      font-size: 24px;
      line-height: 28px;
    }

    &__short-description {
      font-size: 16px;
      line-height: 20px;
      font-weight: 300;
      margin-top: 8px;
    }

    &__volume {
      color: #929199;
      font-size: 18px;
      line-height: 26px;
      margin-top: 8px;
    }

    &__buttons {
      margin-top: 16px;
      display: flex;
      gap: 20px;
      height: 40px;

      a {
        border: 1px solid #000;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #000;
        font-size: 16px;
        line-height: 24px;
        flex: 0 1 50%;
        cursor: pointer;

        &:hover {
          background: #000;
          color: #fff;
          opacity: 1;
        }
      }
    }
  }

  .tabs {
    margin-top: 40px;
    font-size: 16px;
    line-height: 20px;
    color: #7d7b86;

    &__nav,
    &__nav-item {
      border-bottom: 1px solid #dbdbdb;
    }

    &__nav {
      height: 52px;
      display: flex;
      background: #e8e8e8;
    }

    &__nav-item {
      flex: 0 1 calc(100% / 3);
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      &--selected,
      &:hover {
        font-weight: 500;
        color: #000;
      }

      &--selected {
        border-bottom-color: #000;
      }
    }

    &__content {
      margin-top: 21px;
      font-weight: 300;
    }
  }
}

@media (max-width: 1179px) {
  .card {
    width: calc(100% - 80px);

    .info {
      &__title {
        font-size: 19px;
        line-height: 24px;
      }

      &__short-description {
        font-size: 14px;
        line-height: 16px;
      }

      &__volume {
        font-size: 16px;
        line-height: 24px;
      }

      &__buttons {
        gap: 16px;
        height: 32px;

        a {
          font-size: 14px;
          line-height: 20px;
        }
      }
    }

    .tabs {
      margin-top: 32px;
      font-size: 14px;
      line-height: 18px;

      &__nav {
        height: 42px;
      }

      &__content {
        margin-top: 17px;
      }
    }
  }
}

@media (max-width: 599px) {
  .card {
    width: calc(100% - 48px);
    padding: 24px;
    gap: 25px;
    flex-direction: column;

    .info {
      &__title {
        font-size: 16px;
        line-height: 20px;
      }

      &__short-description {
        margin-top: 6px;
        font-size: 12px;
        line-height: 14px;
      }

      &__volume {
        margin-top: 6px;
        font-size: 14px;
        line-height: 20px;
      }

      &__buttons {
        a {
          font-size: 14px;
          line-height: 20px;
        }
      }
    }

    .tabs {
      margin-top: 24px;
      font-size: 14px;
      line-height: 16px;

      &__nav {
        height: 42px;
      }

      &__content {
        font-size: 12px;
        margin-top: 17px;
      }
    }
  }
}
</style>
